/**
 * Client mirror of the server's rank tiers. Kept in sync by hand —
 * the catalog is tiny (7 entries) and stable, so duplicating it
 * spares a network round-trip for every profile / leaderboard render.
 *
 * If the server's `server/src/game/rank.ts` changes, update this file
 * to match.
 */

export interface RankTier {
  tier: number;          // 1..7, low → high
  name: string;
  pointsRequired: number;
  icon: string;
  /** Métal/gemme associé pour la flavour. */
  material: string;
  /** Couleur principale du cadre métallique (hex). */
  tintColor: string;
  /** Variante plus foncée pour le dégradé bas du cadre. */
  tintColorDeep: string;
}

export const RANKS: readonly RankTier[] = [
  { tier: 1, name: 'Chaton baveux',     pointsRequired: 0,    icon: '/sprites/iconchat_neutre.png',         material: 'Cuivre',   tintColor: '#b87333', tintColorDeep: '#7a4a1f' },
  { tier: 2, name: 'Pelote râleuse',    pointsRequired: 120,  icon: '/sprites/iconchat_perdu.png',          material: 'Bronze',   tintColor: '#cd7f32', tintColorDeep: '#8a5318' },
  { tier: 3, name: 'Lécheur de pattes', pointsRequired: 300,  icon: '/sprites/iconchat_interrogation.png', material: 'Fer',      tintColor: '#9aa0a6', tintColorDeep: '#5e6469' },
  { tier: 4, name: 'Maraudeur du sofa', pointsRequired: 550,  icon: '/sprites/iconchat_guerrier.png',       material: 'Argent',   tintColor: '#d6d6d6', tintColorDeep: '#8e8e8e' },
  { tier: 5, name: 'Bourrin du jardin', pointsRequired: 850,  icon: '/sprites/iconchat_lancier.png',        material: 'Or',       tintColor: '#ffd54a', tintColorDeep: '#b88a14' },
  { tier: 6, name: 'Tigre du grenier',  pointsRequired: 1200, icon: '/sprites/iconchat_paladin.png',        material: 'Émeraude', tintColor: '#5fd687', tintColorDeep: '#1f8043' },
  { tier: 7, name: 'Pacha des Lyanx',   pointsRequired: 1700, icon: '/sprites/iconchat_champion.png',       material: 'Diamant',  tintColor: '#7be6ff', tintColorDeep: '#1e6e94' },
];

export interface ResolvedRank {
  tier: RankTier;
  /** Points still needed to reach the next tier (Infinity at the top). */
  pointsToNext: number;
  pointsInTier: number;
  tierBandWidth: number;
}

export function rankFromPoints(points: number): ResolvedRank {
  const safe = Math.max(0, Math.floor(points));
  let tier = RANKS[0]!;
  let nextThreshold = Infinity;
  for (let i = 0; i < RANKS.length; i++) {
    const t = RANKS[i]!;
    if (safe >= t.pointsRequired) {
      tier = t;
      nextThreshold = i + 1 < RANKS.length ? RANKS[i + 1]!.pointsRequired : Infinity;
    } else {
      break;
    }
  }
  const pointsInTier = safe - tier.pointsRequired;
  const tierBandWidth = nextThreshold === Infinity ? 0 : nextThreshold - tier.pointsRequired;
  const pointsToNext = nextThreshold === Infinity ? Infinity : nextThreshold - safe;
  return { tier, pointsToNext, pointsInTier, tierBandWidth };
}
